import type React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

// mui
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import { default as Grid } from '@mui/material/Grid2';
import LinearProgress from '@mui/material/LinearProgress';
import ListItemIcon from '@mui/material/ListItemIcon';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Typography from '@mui/material/Typography';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import CheckCircle from '@mui/icons-material/CheckCircle';
import Undo from '@mui/icons-material/Undo';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import AttachEmailIcon from '@mui/icons-material/AttachEmail';

// notistack
import { enqueueSnackbar } from 'notistack';

import { MaterialReactTable, type MRT_ExpandedState, useMaterialReactTable, type MRT_ColumnDef as ColumnDef } from 'material-react-table';
import Big from 'big.js';

// types
import { type AppConfigRoot, type Invoice, type Payment, type Refund, systemDefaultValues } from '@a1/shared';
import { type AxiosResponse } from 'axios';
// import { type Booking } from 'types/booking';

// constants
import { paymentMethods, paymentStatusAction, paymentStatuses } from 'constants-ts/payments';

// utils
import { useCheckPermission } from 'hooks-ts/usePermission';
import callAzureFunction from 'utils-ts/call-azure-function';
import handleError from 'utils-ts/handle-error';
import callAzureFunctionAuth from 'utils-ts/call-azure-function-auth';
import { formatWithCurrency } from 'utils-ts/sales-and-invoicing';
import { formatDateTime } from 'utils-ts/formatter';

// components
import InvoicePdfDialog from './InvoicePdfDialog';
import RefundDialog from './RefundDialog';
import SendBillingDocumentPdfDialog from './SendBillingDocumentPdfDialog';
import { type Booking } from 'types/booking';

// Sum all refund amounts into a single Big instance
const getRefundTotalBig = (payment: Payment): Big => {
  if (!payment?.Refunds) {
    return Big(0);
  }
  return payment.Refunds.reduce((sum: Big, refund: Refund) => sum.plus(refund.amount || 0), Big(0));
};

const isPaymentBreakdownAvailable = (payment: Payment): payment is Payment & { feeAmount: number; netAmount: number } =>
  payment.feeAmount != null && payment.netAmount != null;

// Total refunded amount = sum of all refund amounts
const getRefundedAmount = (payment: Payment): number => {
  const refundTotalBig = getRefundTotalBig(payment);
  return Number(refundTotalBig.toFixed(2));
};

// Remaining refundable amount = net amount - total refunded amount
const getRefundableAmount = (payment: Payment): number => {
  const netAmountBig = Big(payment.netAmount || 0);
  const refundTotalBig = getRefundTotalBig(payment);
  return Number(netAmountBig.minus(refundTotalBig).toFixed(2));
};

const formatAmountOrUnavailable = (amount: number | null | undefined, currency?: string | null): string => {
  if (amount == null) {
    return 'Payment details not yet available';
  }

  return formatWithCurrency(amount, currency ?? systemDefaultValues.currency);
};

interface PaymentsTableProps {
  payments?: Payment[];
  setSelectedPayment: React.Dispatch<React.SetStateAction<Payment | null>>;
  setOpenRefundDialog: React.Dispatch<React.SetStateAction<boolean>>;
  setPaymentMethod: React.Dispatch<React.SetStateAction<string>>;
  expanded?: MRT_ExpandedState;
  setExpanded?: React.Dispatch<React.SetStateAction<MRT_ExpandedState>>;
  isRefundAllowed: boolean;
}

type PaymentWithExternalChargeRef = Payment;

const getPaymentExternalChargeRef = (payment: Payment): string | undefined =>
  (payment as PaymentWithExternalChargeRef).externalChargeRef ?? undefined;

const PaymentsTable = ({
  payments,
  setSelectedPayment,
  setOpenRefundDialog,
  setPaymentMethod,
  expanded,
  setExpanded,
  isRefundAllowed
}: PaymentsTableProps) => {
  const getAvailableActions = (payment: Payment | null) => {
    if (!payment) {
      return [];
    }

    const actions: { label: string; key: string }[] = [];

    // allow refund as long as remaining refundable amount > 0
    const refundableAmount = getRefundableAmount(payment);
    if (refundableAmount > 0) {
      actions.push({
        label: 'Refund',
        key: paymentStatusAction.REFUNDED
      });
    }

    return actions;
  };

  const columns = useMemo<ColumnDef<Payment>[]>(
    () => [
      {
        accessorKey: 'createdAt',
        header: 'Date & Time',
        Cell: ({ cell }) => {
          const dateValue = cell.getValue<string>();
          if (!dateValue) return '';

          const date = new Date(dateValue);
          return formatDateTime(date);
        }
      },
      {
        accessorKey: 'transactionRef',
        header: 'Reference',
        size: 50
      },
      {
        accessorKey: 'grossAmount',
        header: 'Gross Amount',
        Cell: ({ row }) => {
          const value = row.original.grossAmount;
          const currency = row.original.currency;
          return formatWithCurrency(value, currency ?? systemDefaultValues.currency);
        }
      },
      {
        accessorKey: 'method',
        header: 'Method',
        Cell: ({ cell }) => paymentMethods[cell.getValue<string>()]?.label || ''
      },
      { accessorKey: 'status', header: 'Status', Cell: ({ cell }) => paymentStatuses[cell.getValue<string>()]?.label || '' }
    ],
    []
  );

  const table = useMaterialReactTable({
    columns,
    data: payments ?? [],
    getRowId: (row) => row.id,
    state: { expanded },
    onExpandedChange: setExpanded,
    enableSorting: false,
    enableFilters: false,
    enableTopToolbar: false,
    enableBottomToolbar: false,
    enableColumnActions: false,
    enableRowActions: true,
    muiDetailPanelProps: () => ({
      sx: (theme) => ({
        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,210,244,0.1)' : 'rgba(0,0,0,0.1)'
      })
    }),
    renderRowActionMenuItems: ({ row, closeMenu }) => {
      const payment = row.original;
      const actions = getAvailableActions(payment);
      return actions.map((action) => (
        <MenuItem
          key={action.key}
          onClick={() => {
            setSelectedPayment(payment);
            if (action.key === paymentStatusAction.REFUNDED) {
              if (!isRefundAllowed) {
                enqueueSnackbar({
                  variant: 'error',
                  message: 'You do not have permission to refund payments.',
                  anchorOrigin: { vertical: 'top', horizontal: 'right' }
                });
                return;
              }
              setOpenRefundDialog(true);
              setPaymentMethod(payment.method ?? '');
            }
            closeMenu();
          }}
        >
          <ListItemIcon>
            {(() => {
              if (action.key === paymentStatusAction.MARK_AS_PAID) {
                return <CheckCircle fontSize="small" />;
              }
              return <Undo fontSize="small" />;
            })()}
          </ListItemIcon>
          {action.label}
        </MenuItem>
      ));
    },
    renderDetailPanel: ({ row }) => {
      const externalChargeRef = getPaymentExternalChargeRef(row.original);

      if (!isPaymentBreakdownAvailable(row.original)) {
        return (
          <Box sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Payment details not yet available
            </Typography>
          </Box>
        );
      }

      return (
        <Box sx={{ p: 2 }}>
          <Grid container spacing={3}>
            {/* Column 1: Payment Info */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="h6" gutterBottom>
                Payment Information
              </Typography>
              <Typography variant="body2">
                <strong>Gross:</strong> {formatWithCurrency(row.original.grossAmount, row.original.currency)}
              </Typography>
              <Typography variant="body2">
                <strong>Fee:</strong> {formatAmountOrUnavailable(row.original.feeAmount, row.original.currency)}
              </Typography>
              <Typography variant="body2">
                <strong>Net:</strong> {formatAmountOrUnavailable(row.original.netAmount, row.original.currency)}
              </Typography>
              {(row.original.externalSource || row.original.externalTransactionRef || externalChargeRef) && (
                <Box>
                  <Typography variant="subtitle2" sx={{ mt: 2 }}>
                    Gateway Details
                  </Typography>
                  {row.original.externalSource && (
                    <Typography variant="body2">
                      <strong>Gateway:</strong> {row.original.externalSource}
                    </Typography>
                  )}
                  {row.original.externalTransactionRef && (
                    <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                      <strong>Gateway ID:</strong> {row.original.externalTransactionRef}
                    </Typography>
                  )}
                  {externalChargeRef && (
                    <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                      <strong>Gateway Charge Reference:</strong> {externalChargeRef}
                    </Typography>
                  )}
                </Box>
              )}
            </Grid>

            {/* Column 2: Refund Details */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Stack>
                <Typography variant="h6" gutterBottom>
                  Refund Information
                </Typography>
                <Typography variant="body2">
                  <strong>Total Refunded:</strong> {formatWithCurrency(getRefundedAmount(row.original), row.original.currency)}
                </Typography>
                <Typography variant="body2">
                  <strong>Remaining Refundable Amount:</strong>{' '}
                  {formatWithCurrency(getRefundableAmount(row.original), row.original.currency)}
                </Typography>

                {/* Refunds List */}
                {(() => {
                  if (row.original.Refunds && row.original.Refunds.length > 0) {
                    return (
                      <Stack spacing={1} mt={2}>
                        {row.original.Refunds.map((refund, index) => (
                          <Box
                            key={refund.id}
                            sx={{ p: 2, border: '1px solid', borderColor: 'grey.300', borderRadius: 2, bgcolor: 'background.paper' }}
                          >
                            <Typography variant="subtitle2" color="secondary" gutterBottom>
                              Refund #{index + 1}
                            </Typography>
                            <Typography variant="body2">
                              <strong>Date:</strong> {formatDateTime(new Date(refund.createdAt))}
                            </Typography>
                            <Typography variant="body2">
                              <strong>Amount:</strong> {formatWithCurrency(refund.amount, refund.currency)}
                            </Typography>
                            <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                              <strong>Refund ID:</strong> {refund.transactionRef}
                            </Typography>
                            {refund.externalTransactionRef && refund.externalSource && (
                              <>
                                <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                                  <strong>Gateway:</strong> {refund.externalSource}
                                </Typography>
                                <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                                  <strong>Gateway ID:</strong> {refund.externalTransactionRef}
                                </Typography>
                              </>
                            )}
                          </Box>
                        ))}
                      </Stack>
                    );
                  }
                  if (row.original.status === 'REFUNDED') {
                    return (
                      <Box>
                        <Typography variant="subtitle2">Refund Details</Typography>
                        <Typography variant="body2" color="text.secondary">
                          Refund details not available
                        </Typography>
                      </Box>
                    );
                  }
                  return null;
                })()}
              </Stack>
            </Grid>
          </Grid>
        </Box>
      );
    }
  });

  return <MaterialReactTable table={table} />;
};

interface InvoicePaymentsDialogProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setDisplayInvoices?: React.Dispatch<React.SetStateAction<Invoice[]>>;
  invoiceIds?: (string | undefined)[];
  setBookings?: React.Dispatch<React.SetStateAction<Booking[]>>;
  onSuccess?: () => void;
}

const InvoicePaymentsDialog = ({ open, setOpen, invoiceIds, setDisplayInvoices, setBookings, onSuccess }: InvoicePaymentsDialogProps) => {
  const appConfig = useSelector((state: AppConfigRoot) => state.appConfig?.data);
  const isD365IntegrationEnabled = !!appConfig?.integrations?.d365Finance?.enabled;

  const [loading, setLoading] = useState<boolean>(true);
  const [invoices, setInvoices] = useState<Invoice[]>([]);

  const [invoiceTab, setInvoiceTab] = useState<string>('');
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [openInvoicePdfDialog, setOpenInvoicePdfDialog] = useState<boolean>(false);
  const [openSendInvoicePdfDialog, setOpenSendInvoicePdfDialog] = useState<boolean>(false);

  const [selectedPayment, setSelectedPayment] = useState<Payment | null>(null);
  const [openRefundDialog, setOpenRefundDialog] = useState<boolean>(false);
  const [paymentMethod, setPaymentMethod] = useState<string>('');
  const [buttonLoading, setButtonLoading] = useState<boolean>(false);
  const [externalRefNo, setExternalRefNo] = useState<string>('');
  const [externalSource, setExternalSource] = useState<string>('');
  const [expanded, setExpanded] = useState<MRT_ExpandedState>({});
  const permissionCheck = useCheckPermission();

  const processPaymentRefund = useCallback(
    async (
      paymentId: number,
      additionalData: { externalRefNo?: string; externalSource?: string; refundAmount?: string; createCreditNote?: boolean } = {}
    ) => {
      try {
        setButtonLoading(true);
        const payload = {
          paymentId,
          ...additionalData
        };

        const response = await callAzureFunctionAuth({
          url: `refund-payment`,
          method: 'post',
          data: payload
        });

        const { payment } = response.data as { payment: Payment };

        const { status } = payment.Invoice!;

        setInvoices((prevInvoices: Invoice[]) =>
          prevInvoices.map((invoice: Invoice): Invoice =>
            invoice.id === invoiceTab
              ? {
                  ...invoice,
                  Payments: invoice.Payments?.map((p) => (p.id === payment.id ? payment : p)) ?? [payment]
                }
              : invoice
          )
        );

        setDisplayInvoices?.((prevDisplayInvoices: Invoice[]) =>
          prevDisplayInvoices.map((prevDisplayInvoice) =>
            prevDisplayInvoice.id === invoiceTab
              ? {
                  ...prevDisplayInvoice,
                  status,
                  Payments: prevDisplayInvoice.Payments?.map((p) => (p.id === payment.id ? payment : p)) ?? [payment]
                }
              : prevDisplayInvoice
          )
        );

        setBookings?.((prevBookings: Booking[]) =>
          prevBookings.map((booking) => ({
            ...booking,
            Invoices: booking.Invoices?.map((inv: Invoice) =>
              inv.id === invoiceTab
                ? {
                    ...inv,
                    status,
                    Payments: inv.Payments?.map((p) => (p.id === payment.id ? payment : p)) ?? [payment]
                  }
                : inv
            )
          }))
        );

        onSuccess?.();

        enqueueSnackbar({
          variant: 'success',
          message: `Payment ${paymentId} was refunded successfully.`,
          anchorOrigin: { vertical: 'top', horizontal: 'right' }
        });
      } catch (error) {
        enqueueSnackbar({
          variant: 'error',
          message: handleError(error),
          anchorOrigin: { vertical: 'top', horizontal: 'right' }
        });
      } finally {
        setButtonLoading(false);
        setSelectedPayment(null);
        setOpenRefundDialog(false);
      }
    },
    [invoiceTab, onSuccess, setBookings, setDisplayInvoices]
  );

  const handleRefund = async (
    paymentId: number,
    externalRefNo: string,
    externalSource: string,
    refundAmount: string,
    createCreditNote: boolean
  ) => {
    await processPaymentRefund(paymentId, {
      externalRefNo,
      externalSource,
      refundAmount,
      createCreditNote: isD365IntegrationEnabled || createCreditNote
    });
  };

  const getInvoices = useCallback(async () => {
    try {
      setLoading(true);

      const promises: Promise<AxiosResponse<Invoice>>[] = [];

      invoiceIds?.forEach((invoiceId) => {
        if (invoiceId) {
          promises.push(
            callAzureFunction<Invoice>({
              url: `invoices/${invoiceId}`,
              method: 'get',
              params: {
                include: 'payments,refunds'
              }
            })
          );
        }
      });

      const results = await Promise.all(promises);
      const invoices = results.map((result) => result.data) ?? [];
      setInvoices(invoices);
      setInvoiceTab(invoices[0]?.id ?? '');
    } catch (error) {
      handleError(error);
    } finally {
      setLoading(false);
    }
  }, [invoiceIds]);

  useEffect(() => {
    if (open) {
      getInvoices();
    }
  }, [open, getInvoices]);

  const handleClose = () => {
    setInvoices([]);
    setOpen(false);
  };

  return (
    <>
      <Dialog maxWidth="lg" open={open}>
        <DialogTitle variant="h4">Invoice Payments & Refunds</DialogTitle>
        {loading ? (
          <LinearProgress />
        ) : (
          <DialogContent>
            <Grid container sx={{ maxHeight: '70vh', overflow: 'auto' }}>
              <Grid size={12}>
                <TabContext value={invoiceTab}>
                  <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <TabList
                      onChange={(_, value) => {
                        setInvoiceTab(value);
                      }}
                      aria-label="invoice tabs"
                    >
                      {invoices.map((invoice) => (
                        <Tab key={`tab-${invoice.id}`} label={invoice.invoiceNumber ?? `Invoice [${invoice.id}]`} value={invoice.id} />
                      ))}
                    </TabList>
                  </Box>
                  {invoices.map((invoice) => (
                    <TabPanel key={`tab-panel-${invoice.id}`} value={invoice.id}>
                      <Grid container spacing={2}>
                        <Grid size={12} container spacing={1}>
                          <Button
                            onClick={(event) => {
                              event.stopPropagation();
                              if (!permissionCheck.canExportSendInvoices()) {
                                enqueueSnackbar({
                                  variant: 'error',
                                  message: 'You do not have permission to view invoice PDFs.',
                                  anchorOrigin: { vertical: 'top', horizontal: 'right' }
                                });
                                return;
                              }
                              setSelectedInvoice(invoice);
                              setOpenInvoicePdfDialog(true);
                            }}
                            size="small"
                            startIcon={<PictureAsPdfIcon />}
                          >
                            View Invoice PDF
                          </Button>
                          <Button
                            onClick={(event) => {
                              event.stopPropagation();
                              if (!permissionCheck.canExportSendInvoices()) {
                                enqueueSnackbar({
                                  variant: 'error',
                                  message: 'You do not have permission to email invoices.',
                                  anchorOrigin: { vertical: 'top', horizontal: 'right' }
                                });
                                return;
                              }
                              setSelectedInvoice(invoice);
                              setOpenSendInvoicePdfDialog(true);
                            }}
                            size="small"
                            startIcon={<AttachEmailIcon />}
                          >
                            Email Invoice
                          </Button>
                        </Grid>

                        {/* Refund Totals */}
                        <Grid size={12}>
                          <Typography variant="h6" gutterBottom>
                            Refund Information
                          </Typography>
                          <Stack direction="row" spacing={4}>
                            <Stack direction="column">
                              <Typography variant="body1">
                                <strong>Total Refunded:</strong>
                              </Typography>
                              <Typography variant="body1">
                                {formatAmountOrUnavailable(invoice.totalRefundedAmount, invoice.currency)}
                              </Typography>
                            </Stack>
                            <Stack direction="column">
                              <Typography variant="body1">
                                <strong>Remaining Refundable Amount:</strong>
                              </Typography>
                              <Typography variant="body1">
                                {formatAmountOrUnavailable(invoice.totalRefundableAmount, invoice.currency)}
                              </Typography>
                            </Stack>
                          </Stack>
                        </Grid>

                        <Grid size={12}>
                          <Divider>
                            <Chip label="Payment Transactions" />
                          </Divider>
                        </Grid>

                        <Grid size={12}>
                          {invoice.Payments && invoice.Payments.length > 0 ? (
                            <PaymentsTable
                              payments={invoice.Payments}
                              setSelectedPayment={setSelectedPayment}
                              setOpenRefundDialog={setOpenRefundDialog}
                              setPaymentMethod={setPaymentMethod}
                              expanded={expanded}
                              setExpanded={setExpanded}
                              isRefundAllowed={permissionCheck.canRefundPayment()}
                            />
                          ) : (
                            <Typography>This invoice has no payments.</Typography>
                          )}
                        </Grid>
                      </Grid>
                    </TabPanel>
                  ))}
                </TabContext>
              </Grid>
            </Grid>
          </DialogContent>
        )}

        <DialogActions>
          <Button color="error" variant="text" onClick={handleClose}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* To deprecate, use PdfViewer Dialog instead */}
      <InvoicePdfDialog
        open={openInvoicePdfDialog}
        setOpen={setOpenInvoicePdfDialog}
        invoice={selectedInvoice}
        setOpenInvoiceSend={setOpenSendInvoicePdfDialog}
      />

      <SendBillingDocumentPdfDialog
        open={openSendInvoicePdfDialog}
        setOpen={setOpenSendInvoicePdfDialog}
        billingDocument={selectedInvoice}
      />

      {selectedPayment && (
        <RefundDialog
          open={openRefundDialog}
          onClose={() => {
            setOpenRefundDialog(false);
          }}
          onRefund={handleRefund}
          paymentOption={paymentMethod}
          setExternalRefNo={setExternalRefNo}
          setExternalSource={setExternalSource}
          externalRefNo={externalRefNo}
          externalSource={externalSource}
          targetInfo={{
            id: selectedPayment.id,
            displayIdentifier: `Payment (${selectedPayment.transactionRef})`,
            refundable: getRefundableAmount(selectedPayment),
            currency: selectedPayment.currency
          }}
          loading={buttonLoading}
        />
      )}
    </>
  );
};

export default InvoicePaymentsDialog;
